import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

export interface Message {
  id: string;
  content: string;
  senderId: string;
  receiverId: string;
  read: boolean;
  createdAt: string;
  sender: {
    id: string;
    name: string;
    profileImageUrl: string | null;
  };
  receiver: {
    id: string;
    name: string;
    profileImageUrl: string | null;
  };
}

export interface Conversation {
  conversationId: string;
  otherUser: {
    id: string;
    name: string;
    profileImageUrl: string | null;
  };
  lastMessage: {
    content: string;
    createdAt: string;
    isRead: boolean;
    isSentByMe: boolean;
  };
  unreadCount: number;
}

export interface SendMessageInput {
  receiverId: string;
  content: string;
}

/**
 * Get all conversations
 */
export const getConversations = async (): Promise<Conversation[]> => {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${API_BASE_URL}/messages/conversations`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data.conversations;
};

/**
 * Get messages in a conversation
 */
export const getMessages = async (userId: string): Promise<Message[]> => {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${API_BASE_URL}/messages/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data.messages;
};

/**
 * Send a message
 */
export const sendMessage = async (input: SendMessageInput): Promise<Message> => {
  const token = localStorage.getItem('token');
  const response = await axios.post(
    `${API_BASE_URL}/messages`,
    input,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data.message;
};

/**
 * Get unread message count (1:1 + groups)
 */
export const getUnreadMessageCount = async (): Promise<number> => {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${API_BASE_URL}/messages/unread-count`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.count;
};

// ── Group / multi-user conversations ─────────────────────────────────────────

export interface GroupParticipant {
  id: string;
  name: string;
  profileImageUrl: string | null;
}

export interface GroupConversation {
  conversationId: string;
  name: string;
  participants: GroupParticipant[];
  isGroup: boolean;
  lastMessage: {
    content: string;
    createdAt: string;
    isSentByMe: boolean;
    senderName: string;
  } | null;
  unreadCount: number;
}

export interface GroupMessage {
  id: string;
  conversationId: string;
  content: string;
  senderId: string;
  createdAt: string;
  sender: GroupParticipant;
}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export const getGroupConversations = async (): Promise<GroupConversation[]> => {
  const response = await axios.get(`${API_BASE_URL}/messages/groups`, { headers: authHeaders() });
  return response.data.conversations;
};

export const createGroupConversation = async (
  participantIds: string[],
  name?: string
): Promise<{ id: string }> => {
  const response = await axios.post(
    `${API_BASE_URL}/messages/groups`,
    { participantIds, name },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return { id: response.data.conversation.id };
};

export const getGroupMessages = async (
  conversationId: string
): Promise<{ messages: GroupMessage[]; conversation: { id: string; name: string | null; participants: GroupParticipant[] } }> => {
  const response = await axios.get(`${API_BASE_URL}/messages/groups/${conversationId}`, {
    headers: authHeaders(),
  });
  return response.data;
};

export const sendGroupMessage = async (
  conversationId: string,
  content: string
): Promise<GroupMessage> => {
  const response = await axios.post(
    `${API_BASE_URL}/messages/groups/${conversationId}`,
    { content },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data.message;
};

export const renameConversation = async (
  conversationId: string,
  name: string | null
): Promise<{ id: string; name: string | null }> => {
  const response = await axios.patch(
    `${API_BASE_URL}/messages/groups/${conversationId}/name`,
    { name },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
  );
  return response.data;
};

export const leaveGroupConversation = async (conversationId: string): Promise<void> => {
  await axios.delete(`${API_BASE_URL}/messages/groups/${conversationId}`, {
    headers: authHeaders(),
  });
};

export const deleteDirectConversation = async (otherUserId: string): Promise<void> => {
  await axios.delete(`${API_BASE_URL}/messages/direct/${otherUserId}`, {
    headers: authHeaders(),
  });
};
